import { memo, useState, useEffect, useRef } from "react";
import { Input, Tooltip } from "@fluentui/react-components";
import PropTypes from "prop-types";

const EditableDecimalCell = ({
  dataItem,
  field,
  onChange,
  emptyAsNull,
  disabled,
  disableReason,
}) => {
  const [value, setValue] = useState(dataItem[field] == null ? "" : dataItem[field]);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(dataItem[field] == null ? "" : dataItem[field]);
  }, [dataItem, field]);

  const handleChange = (e) => setValue(e.target.value);

  const handleBlur = () => {
    const trimmed = value.trim?.() ?? value;
    const parsed =
      trimmed === ""
        ? emptyAsNull
          ? null
          : 0
        : Math.max(0, parseFloat(trimmed) || 0);

    if (parsed !== dataItem[field]) {
      onChange({ dataItem, field, value: parsed });
    }
  };

  const getTooltipContent = () => {
    if (!disabled) {
      return "";
    }

    if (disableReason === "overdue") {
      return "Please extend the Due Date of the Deal to log hours";
    }
    if (disableReason === "notProjectMember") {
      return "To Edit the Deal, please join it first by reaching out to a Staffer or a Member of the Deal to get added";
    }
    if (disableReason === "notOwner") {
      return "Only the Deal Owner can edit hours for this Deal";
    }
    
    return "This field is currently disabled";
  };

  if (dataItem?.field && dataItem?.aggregates) {
    return <td className="background-white k-table-td" />;
  }

  return (
    <td className="background-white k-table-td">
      <Tooltip
        key={disabled ? "t-disabled" : "t-enabled"}
        content={getTooltipContent()}
        relationship="label"
        visible={disabled ? undefined : false}
      >
        <Input
          type="number"
          min={0}
          step={1}
          placeholder="—"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          appearance="outline"
            onMouseDown={(e) => {
            e.currentTarget.focus({ preventScroll: true }); // focus without scrolling
          }}
          ref={inputRef}
          style={{ flexGrow: 1, minWidth: 0, width: "100%", boxSizing: "border-box" }}
        />
      </Tooltip>
    </td>
  );
};

EditableDecimalCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  emptyAsNull: PropTypes.bool,
  disabled: PropTypes.bool,
  disableReason: PropTypes.oneOf(["overdue", "notProjectMember", "notOwner"]),
};

EditableDecimalCell.defaultProps = {
  emptyAsNull: false,
  disabled: false,
};

export default memo(
  EditableDecimalCell,
  (prev, next) => {
    const prevVal = prev.dataItem[prev.field];
    const nextVal = next.dataItem[next.field];
    const prevOverdue = !!prev.dataItem?.IsOverdue;
    const nextOverdue = !!next.dataItem?.IsOverdue;
    return (
      prevVal === nextVal &&
      prev.disabled === next.disabled &&
      prev.disableReason === next.disableReason &&
      prev.emptyAsNull === next.emptyAsNull &&
      prevOverdue === nextOverdue 
    );
  }
);